"use client";

import React, { useState } from "react";
import { Transition } from "@headlessui/react";
import classNames from "classnames";
import dynamic from "next/dynamic";

import publicConfig from "@/config/public";
import { RetailType } from "@/custom-types/Store";
import { MapData, Store } from "@/custom-types/StoreLocator";

import MarkerSprites from "./MarkerSprites/MarkerSprites";

const MapOverlays = dynamic(() => import("./MapOverlays/MapOverlays"), {
  ssr: false,
});

const MapComponent = dynamic(() => import("./MapComponent"), {
  ssr: false,
});

const {
  imgix: { publicUrl },
} = publicConfig;

const getPlaceholderImgURL = (size: "sm" | "md" | "lg") => {
  const w = { lg: 1025, md: 768, sm: 640 }[size];
  const h = (w * 9) / 16;
  return `${publicUrl}/web-web/finder/finder-map-placeholder.jpg?auto=compress,format&fit=crop&w=${w}&h=${h}&dpr=1`;
};

const MapPlaceholder = () => {
  return (
    <picture>
      <source media="(max-width:768px)" srcSet={getPlaceholderImgURL("sm")} />
      <source media="(max-width:1025px)" srcSet={getPlaceholderImgURL("md")} />
      <img
        src={getPlaceholderImgURL("lg")}
        className="object-cover object-left h-full w-full"
        alt="Loading map..."
        fetchPriority="high"
      />
    </picture>
  );
};

const MapSection: React.FC<{
  initMapData: MapData;
  retailType: RetailType;
  spotlightStores: Store[];
  stores: Store[];
}> = ({ initMapData, retailType, spotlightStores, stores }) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  return (
    <div
      className={classNames(
        "fixed bg-white md:bottom-0",
        "md:left-[var(--stores-desktop-listing-width)] md:top-[var(--header-height)]",
        "inset-[var(--header-height)_0_var(--stores-mobile-bottom-touchpoint)_0]",
      )}
    >
      <Transition show={!mapLoaded}>
        <div className="absolute top w-full h-full transition duration-1000 data-[closed]:opacity-0">
          <MapPlaceholder />
        </div>
      </Transition>
      <div
        className={classNames(
          "h-full w-full transition delay-300 duration-1000",
          {
            "opacity-0": !mapLoaded,
          },
        )}
      >
        <MapComponent
          retailType={retailType}
          stores={stores}
          initMapData={initMapData}
          setMapLoaded={setMapLoaded}
          mapLoaded={mapLoaded}
        />
      </div>
      {spotlightStores.length > 0 && (
        <MapOverlays spotlightStores={spotlightStores} />
      )}
      <MarkerSprites />
    </div>
  );
};

export default MapSection;
